import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, SlidersHorizontal, AlertTriangle } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { FilterChip } from '@/components/shared/filter-chip';
import { CustomerFilter } from './customer-filter';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import { useMerchants } from '@/hooks/use-merchants';
import { useIqServices } from '@/hooks/use-iq-services';
import type { FlowType, Service } from '@/types/service';
import type { ServiceFilters } from '@/hooks/use-service-filter-params';

const FLOW_TYPES: FlowType[] = ['WAP_CLICK', 'WEB_TAN'];

// The merchant facet sources its options from the FULL merchant list, not the
// page-local loaded services (Codex #1). The merchant listing caps `limit` at 100
// (merchant-handler); for this institute's merchant count that is the accepted
// full-list ceiling, so request the cap rather than the server default (50).
const MERCHANT_FACET_LIMIT = 100;

interface ServiceFilterBarProps {
  filters: ServiceFilters;
  hasActiveFilters: boolean;
  onSetMerchant: (value: string | undefined) => void;
  onSetGw: (value: string | undefined) => void;
  onSetFlow: (value: string | undefined) => void;
  onSetIqServiceId: (value: string | undefined) => void;
  onReset: () => void;
  /** Loaded page services — best-effort, client-side suggestion source for gw / iqServiceId. */
  services: Service[];
  /** Scan-mode degradation flags echoed from the BFF response. */
  truncated?: boolean;
  unavailable?: boolean;
}

/**
 * Spec 352 §3: search-led filter bar with progressive disclosure + removable
 * active-filter chips. Such-gefuehrt is realised two ways: the leading quick-find
 * Input narrows the gw / iqServiceId suggestion lists CLIENT-SIDE (no new query
 * param), and the secondary credential-set facet hides behind a "more filters"
 * toggle. The four facets (merchant / flow / gw / iqServiceId) are the only filter
 * dimensions; each maps to one URL param via useServiceFilterParams.
 */
export function ServiceFilterBar({
  filters,
  hasActiveFilters,
  onSetMerchant,
  onSetGw,
  onSetFlow,
  onSetIqServiceId,
  onReset,
  services,
  truncated,
  unavailable,
}: ServiceFilterBarProps) {
  const { t } = useTranslation('services');
  const { showMerchant } = useMerchantScope();
  const [quickFind, setQuickFind] = useState('');
  const [showMore, setShowMore] = useState(false);

  // Full merchant source (NOT page-local) — a merchant absent from the loaded
  // service page must still be selectable. Gated: merchant roles never fetch.
  const merchantsQuery = useMerchants(
    { limit: MERCHANT_FACET_LIMIT },
    { enabled: showMerchant },
  );
  const merchantIds = useMemo(
    () => (merchantsQuery.data?.data ?? []).map((m) => m.merchantId),
    [merchantsQuery.data],
  );
  const showMerchantFacet = showMerchant && merchantIds.length > 1;

  // Platform-only gateway-config list — the HOOK CALL is gated (enabled), not just
  // the render: an unconditional mount would 403 for every merchant role.
  const iqServicesQuery = useIqServices({ enabled: showMerchant });

  // gw suggestions: distinct gatewayServiceIds from the loaded page's chains.
  const gwOptions = useMemo<ComboboxOption[]>(() => {
    const seen = new Set<string>();
    const opts: ComboboxOption[] = [];
    for (const svc of services) {
      for (const entry of svc.fallbackChain ?? []) {
        if (entry.gatewayServiceId && !seen.has(entry.gatewayServiceId)) {
          seen.add(entry.gatewayServiceId);
          opts.push({ value: entry.gatewayServiceId, label: entry.gatewayServiceId });
        }
      }
    }
    return opts;
  }, [services]);

  // iqServiceId suggestions: platform → the gateway-config list; merchant → the
  // distinct iqServiceIds present on their own loaded chains (free-text always works).
  const iqServiceOptions = useMemo<ComboboxOption[]>(() => {
    if (showMerchant) {
      return (iqServicesQuery.data?.data ?? []).map((svc) => ({
        value: svc.iqServiceId,
        label: svc.displayName,
        description: `${svc.iqServiceId} · ${svc.merchantName} · ${svc.gatewayMode}`,
      }));
    }
    const seen = new Set<string>();
    const opts: ComboboxOption[] = [];
    for (const svc of services) {
      for (const entry of svc.fallbackChain ?? []) {
        if (entry.iqServiceId && !seen.has(entry.iqServiceId)) {
          seen.add(entry.iqServiceId);
          opts.push({ value: entry.iqServiceId, label: entry.iqServiceId });
        }
      }
    }
    return opts;
  }, [showMerchant, iqServicesQuery.data, services]);

  const narrow = (opts: ComboboxOption[]): ComboboxOption[] => {
    const q = quickFind.trim().toLowerCase();
    if (!q) return opts;
    return opts.filter((o) =>
      `${o.label} ${o.value} ${o.description ?? ''}`.toLowerCase().includes(q),
    );
  };

  const flowLabel = t('form.label.flowType');
  const gwLabel = t('form.label.gatewayServiceId');
  const iqLabel = t('form.label.iqServiceId');

  const degraded = truncated || unavailable;

  return (
    <div className="mb-4 space-y-3" role="search">
      <div className="flex flex-wrap items-end gap-3">
        {/* Leading quick-find — narrows the gw / iqServiceId suggestions (client-side). */}
        <div>
          <Label htmlFor="service-filter-search" className="text-xs text-muted-foreground">
            {t('filter.search')}
          </Label>
          <Input
            id="service-filter-search"
            value={quickFind}
            onChange={(e) => setQuickFind(e.target.value)}
            placeholder={t('filter.search')}
            className="w-full sm:w-56"
          />
        </div>

        {showMerchantFacet && (
          <div>
            <Label className="text-xs text-muted-foreground">{t('filter.customerPlaceholder')}</Label>
            <CustomerFilter
              merchantIds={merchantIds}
              value={filters.merchantId ?? ''}
              onChange={(v) => onSetMerchant(v || undefined)}
            />
          </div>
        )}

        <div>
          <Label className="text-xs text-muted-foreground">{flowLabel}</Label>
          <Select
            value={filters.flowType ?? 'all'}
            onValueChange={(v) => onSetFlow(v === 'all' ? undefined : v)}
          >
            <SelectTrigger className="w-[150px]" aria-label={flowLabel}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filter.flowAll')}</SelectItem>
              {FLOW_TYPES.map((flow) => (
                <SelectItem key={flow} value={flow}>
                  {flow}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs text-muted-foreground">{gwLabel}</Label>
          <Combobox
            options={narrow(gwOptions)}
            value={filters.gatewayServiceId ?? ''}
            onChange={(v) => onSetGw(v || undefined)}
            allowFreeText
            placeholder={t('filter.gw')}
            className="w-full sm:w-64"
          />
        </div>

        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={() => setShowMore((s) => !s)}
          aria-expanded={showMore}
        >
          <SlidersHorizontal className="mr-1.5 h-4 w-4" />
          {t('filter.moreFilters')}
        </Button>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={onReset}>
            <X className="mr-1.5 h-4 w-4" />
            {t('filter.reset')}
          </Button>
        )}
      </div>

      {showMore && (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">{iqLabel}</Label>
            <Combobox
              options={narrow(iqServiceOptions)}
              value={filters.iqServiceId ?? ''}
              onChange={(v) => onSetIqServiceId(v || undefined)}
              allowFreeText
              loading={showMerchant && iqServicesQuery.isLoading}
              placeholder={t('filter.gatewayConfig')}
              className="w-full sm:w-72"
            />
          </div>
        </div>
      )}

      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2">
          {showMerchant && filters.merchantId && (
            <FilterChip
              label={filters.merchantId}
              onRemove={() => onSetMerchant(undefined)}
              removeLabel={t('filter.chipRemove')}
            />
          )}
          {filters.flowType && (
            <FilterChip
              label={`${flowLabel}: ${filters.flowType}`}
              onRemove={() => onSetFlow(undefined)}
              removeLabel={t('filter.chipRemove')}
            />
          )}
          {filters.gatewayServiceId && (
            <FilterChip
              label={`${gwLabel}: ${filters.gatewayServiceId}`}
              onRemove={() => onSetGw(undefined)}
              removeLabel={t('filter.chipRemove')}
            />
          )}
          {filters.iqServiceId && (
            <FilterChip
              label={`${iqLabel}: ${filters.iqServiceId}`}
              onRemove={() => onSetIqServiceId(undefined)}
              removeLabel={t('filter.chipRemove')}
            />
          )}
        </div>
      )}

      {degraded && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{unavailable ? t('banner.unavailable') : t('banner.truncated')}</span>
        </div>
      )}
    </div>
  );
}
